/**
 * QuickTest sample-field ordering — extracted from `QuickTest.tsx` so it
 * can be unit-tested without pulling React into the test graph.
 *
 * The form renders ONLY the fields a rule's order lists (see QuickTest.tsx),
 * so a rule whose outcome is driven by `cart_total` / `cart_currency` /
 * `quoted_total` / `principal` MUST list that field here, or the merchant
 * loads a preset whose driving value is invisible and uneditable. The
 * `quicktest-fields-drift` test enforces exactly that against the presets.
 */

import type { RuleType } from '@sill/types';

export type FieldKey =
  | 'agent_id'
  | 'action'
  | 'merchant'
  | 'sku'
  | 'amount_currency'
  | 'validity_seconds'
  | 'cart_total'
  | 'cart_currency'
  | 'quoted_total'
  | 'skill_manifest_hash'
  | 'principal'
  | 'delegation_chain';

export const DEFAULT_FIELD_ORDER: readonly FieldKey[] = [
  'agent_id',
  'action',
  'merchant',
  'sku',
  'amount_currency',
  'validity_seconds',
];

// The form renders ONLY the fields a rule's order lists, so the cart /
// quote / principal inputs surface exclusively for the rules that read
// them (and lead, so cause-and-effect is obvious). Rules driven by the
// six default fields don't carry the extra inputs as noise.
export const FIELD_ORDER_BY_RULE: Partial<Record<RuleType, readonly FieldKey[]>> = {
  // r01 (agent allowlist) reads ONLY agent_id — lead with it (without an entry
  // it fell back to DEFAULT_FIELD_ORDER, burying the one field that matters
  // fourth). Review 2026-06-10.
  r01: ['agent_id', 'action', 'merchant', 'sku', 'amount_currency', 'validity_seconds'],
  // r02 gates on the principal (human + delegated = user-signed
  // IntentMandate). The principal is now an editable field, so it leads.
  r02: ['principal', 'action', 'agent_id', 'merchant', 'sku', 'amount_currency', 'validity_seconds'],
  r05: ['amount_currency', 'action', 'merchant', 'sku', 'agent_id', 'validity_seconds'],
  // r07 fires on action + amount/currency together (destructive action,
  // then USD-over-cap or non-USD fail-safe), so both lead.
  r07: ['action', 'amount_currency', 'merchant', 'sku', 'agent_id', 'validity_seconds'],
  // r08 scans sku / merchant / action for urgency phrasing; lead with the
  // string fields the urgency phrase lands in.
  r08: ['sku', 'merchant', 'action', 'agent_id', 'amount_currency', 'validity_seconds'],
  // r09 (no drip pricing) compares quoted_total vs cart_total — both now
  // editable, both lead. max_amount is irrelevant to r09 (it rides last).
  r09: ['quoted_total', 'cart_total', 'action', 'merchant', 'sku', 'agent_id', 'amount_currency', 'validity_seconds'],
  r10: ['action', 'sku', 'agent_id', 'merchant', 'amount_currency', 'validity_seconds'],
  // r14 (cart ≤ intent ceiling) compares cart_total vs max_amount — lead
  // with cart_total then the amount/currency block (which carries
  // max_amount) so the comparison is visible side by side.
  r14: ['cart_total', 'amount_currency', 'action', 'merchant', 'sku', 'agent_id', 'validity_seconds'],
  // r15 (currency must match) compares cart_currency vs intent currency —
  // lead with cart_currency then the amount/currency block (intent currency).
  r15: ['cart_currency', 'amount_currency', 'action', 'merchant', 'sku', 'agent_id', 'validity_seconds'],
  // r19 fires on the action (create_subscription) gated by the principal —
  // action scopes it, principal is the consent gate, so both lead.
  r19: ['action', 'principal', 'agent_id', 'merchant', 'sku', 'amount_currency', 'validity_seconds'],
  // r18 (skill-manifest integrity) compares the signed manifest digest vs the
  // approved allow-list — lead with the digest field so the merchant edits
  // what drives the outcome.
  r18: ['skill_manifest_hash', 'action', 'agent_id', 'merchant', 'sku', 'amount_currency', 'validity_seconds'],
  r20: ['action', 'sku', 'agent_id', 'merchant', 'amount_currency', 'validity_seconds'],
  r22: ['action', 'sku', 'agent_id', 'merchant', 'amount_currency', 'validity_seconds'],
  r23: ['validity_seconds', 'action', 'agent_id', 'merchant', 'sku', 'amount_currency'],
  r25: ['sku', 'action', 'agent_id', 'merchant', 'amount_currency', 'validity_seconds'],
  // r29 (delegation-chain policy) fires on the chain (presence / depth /
  // head) gated by the principal's verification — both lead. The chain
  // field synthesizes an UNSIGNED chain server-side; signature validity is
  // the verifier's job and isn't what this rule polices.
  r29: ['delegation_chain', 'principal', 'action', 'agent_id', 'merchant', 'sku', 'amount_currency', 'validity_seconds'],
};

export function fieldOrderFor(type: RuleType): readonly FieldKey[] {
  return FIELD_ORDER_BY_RULE[type] ?? DEFAULT_FIELD_ORDER;
}

/**
 * The field(s) a rule's predicate ACTUALLY reads — the ones that move the
 * outcome. Derived from the rule handlers (review 2026-06-10). Several rules
 * have more than one driver: r05/r07 also gate on `currency` (the amount block
 * is one field), r08/r10/r20/r22 scan action+merchant+sku equally, r09/r14/r15
 * compare two fields, and r25 reads the whole body so EVERY string field counts.
 *
 * QuickTest marks these fields and collapses the remaining (inert-for-this-rule)
 * context fields, so the merchant isn't editing inputs that can't change the
 * result. `undefined` ⇒ unknown drivers (r_custom DSL, r28 kill switch, …) ⇒
 * the panel shows all fields un-split (no false claim about what matters).
 */
export const DRIVERS_BY_RULE: Partial<Record<RuleType, readonly FieldKey[]>> = {
  r01: ['agent_id'],
  r02: ['principal'],
  r05: ['amount_currency'],
  r07: ['action', 'amount_currency'],
  r08: ['sku', 'merchant', 'action'],
  r09: ['quoted_total', 'cart_total'],
  r10: ['action', 'sku', 'merchant'],
  r14: ['cart_total', 'amount_currency'],
  r15: ['cart_currency', 'amount_currency'],
  r18: ['skill_manifest_hash'],
  r19: ['action', 'principal'],
  r20: ['sku', 'merchant', 'action'],
  r22: ['sku', 'merchant', 'action'],
  r23: ['validity_seconds'],
  // r25 reads the serialized body byte length — every string field contributes,
  // so nothing is inert; mark them all so none gets collapsed.
  r25: ['sku', 'action', 'merchant', 'agent_id', 'amount_currency', 'validity_seconds'],
  r29: ['delegation_chain', 'principal'],
};

/** Driver field set for a rule, or null when the drivers are unknown (the
 *  panel then shows all fields without a driver/context split). */
export function driversFor(type: RuleType): ReadonlySet<FieldKey> | null {
  const d = DRIVERS_BY_RULE[type];
  return d ? new Set(d) : null;
}
